import { flowKindColor } from './flowColors'
import type { FlowFocus } from './flowFocus'
import {
  type FlowValueFormat,
  formatCount,
  formatFlowAmount,
  formatShare,
} from './flowFormat'
import type { FlowLayout, LaidOutFlowLink, LaidOutFlowNode } from './flowLayout'
import type { FlowKind, FlowOfFunds, FlowRole } from './flowModel'
import { type FlowSide, kindTotals, nodeTotals } from './flowTotals'

interface FlowTooltipProps {
  focus: FlowFocus
  flow: FlowOfFunds
  layout: FlowLayout
  valueFormat: FlowValueFormat
}

// What a link's share is measured against.
const SHARE_BASE: Record<FlowKind, { kind: FlowKind; label: string }> = {
  in: { kind: 'in', label: 'of money in' },
  kept: { kind: 'in', label: 'of money in' },
  between: { kind: 'between', label: 'of transfers between own accounts' },
  out: { kind: 'out', label: 'of money out' },
}

const SIDE_LABELS: Record<FlowRole, { in: string; out: string }> = {
  payer: { in: 'In', out: 'Paid in' },
  received: { in: 'In', out: 'Out' },
  spent: { in: 'In', out: 'Out' },
  account: { in: 'Came in', out: 'Paid out' },
  from: { in: 'In', out: 'Sent' },
  to: { in: 'Received', out: 'Out' },
  payee: { in: 'Paid out', out: 'Out' },
}

export function FlowTooltip({
  focus,
  flow,
  layout,
  valueFormat,
}: FlowTooltipProps) {
  if (!focus) return null
  if (focus.type === 'link') {
    const link = layout.links[focus.index]
    if (!link) return null
    return <LinkDetail link={link} flow={flow} valueFormat={valueFormat} />
  }
  const node = layout.nodes.find((candidate) => candidate.name === focus.name)
  if (!node) return null
  return <NodeDetail node={node} flow={flow} valueFormat={valueFormat} />
}

function transactions(count: number): string {
  return `${formatCount(count)} transaction${count === 1 ? '' : 's'}`
}

function LinkDetail({
  link,
  flow,
  valueFormat,
}: {
  link: LaidOutFlowLink
  flow: FlowOfFunds
  valueFormat: FlowValueFormat
}) {
  const source = link.source as LaidOutFlowNode
  const target = link.target as LaidOutFlowNode
  const route =
    link.kind === 'kept'
      ? `Stayed in ${source.label}`
      : `${source.label} → ${target.label}`
  const base = SHARE_BASE[link.kind]
  const baseAmount = kindTotals(flow)[base.kind].amount
  return (
    <div className="space-y-0.5 py-0.5">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <span
          aria-hidden
          className="h-0.5 w-3 rounded-full"
          style={{ backgroundColor: flowKindColor(link.kind) }}
        />
        {route}
      </div>
      <div className="text-sm font-semibold tabular-nums text-foreground">
        {formatFlowAmount(link.value, valueFormat)}
      </div>
      <div className="text-muted-foreground">
        {link.count > 0 && `${transactions(link.count)} · `}
        {formatShare(link.value, baseAmount)} {base.label}
      </div>
    </div>
  )
}

function NodeDetail({
  node,
  flow,
  valueFormat,
}: {
  node: LaidOutFlowNode
  flow: FlowOfFunds
  valueFormat: FlowValueFormat
}) {
  const totals = nodeTotals(flow).get(node.name)
  if (!totals) return null
  const labels = SIDE_LABELS[node.role]
  const sides = [
    { label: labels.in, side: totals.in },
    { label: labels.out, side: totals.out },
  ].filter((entry) => entry.side.amount > 0)
  return (
    <div className="space-y-0.5 py-0.5">
      <div className="text-muted-foreground">{node.label}</div>
      {sides.map((entry) => (
        <SideRow
          key={entry.label}
          label={entry.label}
          side={entry.side}
          valueFormat={valueFormat}
        />
      ))}
    </div>
  )
}

function SideRow({
  label,
  side,
  valueFormat,
}: {
  label: string
  side: FlowSide
  valueFormat: FlowValueFormat
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {formatFlowAmount(side.amount, valueFormat)}
      </span>
      <span className="text-muted-foreground">
        {label}
        {side.count > 0 && ` · ${transactions(side.count)}`}
      </span>
    </div>
  )
}
